import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle, Share2, Clock, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LikeButton } from "@/components/LikeButton";
import { CommentSection } from "@/components/CommentSection";
import { useAuth } from "@/context/AuthContext";
import { toHijri } from "@/lib/hijri";
import { timeAgo } from "@/lib/timeago";
import { useLang } from "@/lib/i18n";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const FeedCard = ({ item, onDelete }) => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);
  const [commentsCount, setCommentsCount] = useState(item.comments_count || 0);
  const d = item.data;
  const locale = lang === "fr" ? "fr-FR" : "en-US";
  const canDeletePost = item.type === "post" && user && (
    user.id === d.author_id || user.role === "super_admin" ||
    (["association_admin", "moderator"].includes(user.role) && user.association_slug === item.assoc_slug)
  );

  const removePost = async () => {
    try {
      await api.delete(`/associations/${item.assoc_slug}/posts/${item.id}`);
      toast.success(t("post_deleted"));
      onDelete?.(item.id);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const link = item.type === "event" ? `/${item.assoc_slug}/events/${item.id}`
    : item.type === "photo" ? `/${item.assoc_slug}/gallery`
    : `/${item.assoc_slug}`;

  const typeLabel = item.type === "event" ? t("event") : item.type === "photo" ? t("photo") : item.type === "post" ? t("post") : t("announcement");

  const share = async () => {
    const url = window.location.origin + link;
    const title = item.type === "photo" ? (d.caption || "Photo") : d.title;
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success(t("link_copied"));
    }
  };

  const start = item.type === "event" ? new Date(d.start) : null;
  const hijri = item.type === "event" ? toHijri(d.start, item.hijri_offset || 0) : null;

  return (
    <article data-testid="feed-card" className="border border-border rounded-xl bg-card overflow-hidden fade-up">
      <div className="flex items-center gap-3 px-4 py-3">
        <Link to={`/${item.assoc_slug}`}>
          <Avatar className="h-9 w-9 border border-border">
            <AvatarImage src={item.assoc_logo} alt={item.assoc_name} />
            <AvatarFallback className="text-xs font-bold text-white" style={{ background: item.assoc_color }}>
              {item.assoc_name?.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </Link>
        <div className="min-w-0 flex-1">
          <Link to={`/${item.assoc_slug}`} data-testid="feed-assoc-link" className="font-heading font-semibold text-sm hover:underline block truncate">
            {item.assoc_name}
          </Link>
          <p className="text-[11px] font-mono-data text-muted-foreground">
            {item.type === "post" && d.author_name ? `${d.author_name} · ` : ""}{timeAgo(item.created_at, lang)}
          </p>
        </div>
        {canDeletePost && (
          <button data-testid="feed-post-delete-btn" onClick={removePost} className="text-muted-foreground hover:text-destructive transition-colors shrink-0">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
        <Badge variant="secondary" className="text-[10px] uppercase tracking-wide shrink-0">{typeLabel}</Badge>
      </div>

      {item.type === "event" && (
        <button data-testid="feed-event-body" onClick={() => navigate(link)} className="w-full text-left">
          {(d.images?.[0] || d.image) && <img src={d.images?.[0] || d.image} alt={d.title} className="w-full max-h-72 object-cover" />}
          <div className="px-4 pt-3">
            <h3 className="font-heading font-bold text-lg leading-snug">{d.title}</h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mt-1.5">
              <span className="capitalize font-medium">{start.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" })}</span>
              <span className="font-mono-data">{hijri.label} AH</span>
              <span className="flex items-center gap-1 font-mono-data"><Clock className="h-3 w-3" />{start.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}</span>
              {d.mosque_name && (
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full" style={{ background: d.mosque_color }} />{d.mosque_name}
                </span>
              )}
            </div>
            {d.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{d.description}</p>}
          </div>
        </button>
      )}

      {item.type === "announcement" && (
        <div className="px-4">
          <h3 className="font-heading font-bold text-lg leading-snug">{d.title}</h3>
          <p className="text-sm mt-1.5 whitespace-pre-wrap">{d.content}</p>
        </div>
      )}

      {item.type === "post" && (
        <div className="px-4" data-testid="feed-post-body">
          {d.content && <p className="text-sm whitespace-pre-wrap">{d.content}</p>}
          {d.image && <img src={d.image} alt="" loading="lazy" className="w-full rounded-lg mt-3 max-h-[480px] object-cover" />}
        </div>
      )}

      {item.type === "photo" && (
        <button data-testid="feed-photo-body" onClick={() => navigate(link)} className="w-full">
          <img src={`${BACKEND}/api/gallery/${item.id}/image`} alt={d.caption || "photo"} loading="lazy" className="w-full max-h-[480px] object-cover" />
          {d.caption && <p className="text-sm text-left px-4 pt-2.5">{d.caption}</p>}
        </button>
      )}

      <div className="flex items-center gap-5 px-4 py-3 mt-1">
        <LikeButton targetType={item.type} targetId={item.id} liked={item.liked} count={item.likes_count} />
        <button
          data-testid="feed-comment-btn"
          onClick={() => setShowComments((s) => !s)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <MessageCircle className="h-[18px] w-[18px]" />
          {commentsCount > 0 && <span className="font-mono-data text-xs">{commentsCount}</span>}
        </button>
        <button data-testid="feed-share-btn" onClick={share} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <Share2 className="h-[18px] w-[18px]" />
        </button>
      </div>

      {showComments && (
        <div className="px-4 pb-4 border-t border-border pt-3">
          <CommentSection targetType={item.type} targetId={item.id} onCountChange={setCommentsCount} />
        </div>
      )}
    </article>
  );
};
